import { useState } from "react";

import { cn } from "@/lib/utils";

import { GradeBadge } from "./KpiTile";
import { Bar, Card } from "./FunnelCard";
import {
  SEGMENT_DIMS,
  lostLabel,
  methodLabel,
  segmentLabel,
  sourceLabel,
} from "./labels";
import {
  adRows,
  euros,
  grade,
  percent,
  rate,
  segmentsOf,
  type Performance,
} from "./performance";

/** Fewer leads than this: the % is shown but not graded (too little sample). */
const MIN_SAMPLE = 15;

const Th = ({
  children,
  right = false,
}: {
  children: React.ReactNode;
  right?: boolean;
}) => (
  <th
    className={cn(
      "whitespace-nowrap px-2 py-1.5 text-xs font-medium text-muted-foreground",
      right ? "text-right" : "text-left",
    )}
  >
    {children}
  </th>
);

const Td = ({
  children,
  right = false,
}: {
  children: React.ReactNode;
  right?: boolean;
}) => (
  <td
    className={cn(
      "px-2 py-1.5 tabular-nums",
      right ? "text-right" : "text-left",
    )}
  >
    {children}
  </td>
);

const CloseCell = ({ sold, leads }: { sold: number; leads: number }) => {
  const value = rate(sold, leads);
  return (
    <span className="inline-flex items-center justify-end gap-1.5">
      {percent(value)}
      {leads >= MIN_SAMPLE ? (
        <GradeBadge value={grade("closeRate", value)} />
      ) : null}
    </span>
  );
};

/** Each ad: leads, how many we talked to and sold, and what it cost. */
export const AdsCard = ({ perf }: { perf: Performance }) => {
  const rows = adRows(perf);
  const hasSpend = !!perf.spend;
  return (
    <Card
      title="Anuncios"
      hint={
        hasSpend
          ? "Leads del periodo por anuncio, con lo que costó cada uno en Meta"
          : "Leads del periodo por anuncio. El coste saldrá cuando Meta esté conectado"
      }
    >
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b">
              <Th>Anuncio</Th>
              <Th right>Leads</Th>
              <Th right>Hablados</Th>
              <Th right>Ventas</Th>
              <Th right>Cierre</Th>
              {hasSpend ? (
                <>
                  <Th right>Gasto</Th>
                  <Th right>€/lead</Th>
                  <Th right>€/venta</Th>
                </>
              ) : null}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.key} className="border-b last:border-0">
                <Td>{row.key}</Td>
                <Td right>{row.leads}</Td>
                <Td right>{percent(rate(row.talked, row.leads))}</Td>
                <Td right>{row.sold}</Td>
                <Td right>
                  <CloseCell sold={row.sold} leads={row.leads} />
                </Td>
                {hasSpend ? (
                  <>
                    <Td right>{euros(row.spend)}</Td>
                    <Td right>{euros(row.costPerLead, 2)}</Td>
                    <Td right>{euros(row.costPerSale)}</Td>
                  </>
                ) : null}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
};

/** The leads split by what we know of them: which kind of lead buys. */
export const SegmentsCard = ({ perf }: { perf: Performance }) => {
  const [dim, setDim] = useState(SEGMENT_DIMS[0].key);
  const rows = segmentsOf(perf, dim);
  const max = Math.max(...rows.map((row) => row.leads), 1);
  const hint = SEGMENT_DIMS.find((d) => d.key === dim)?.hint;
  return (
    <Card
      title="Qué tipo de lead compra"
      hint="Leads del periodo según lo que sabemos de ellos"
    >
      <div className="flex flex-wrap gap-1.5">
        {SEGMENT_DIMS.map((option) => (
          <button
            key={option.key}
            type="button"
            onClick={() => setDim(option.key)}
            className={cn(
              "rounded-full border px-3 py-1 text-xs",
              dim === option.key
                ? "border-foreground bg-foreground text-background"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            {option.label}
          </button>
        ))}
      </div>
      {hint ? <p className="text-xs text-muted-foreground">{hint}</p> : null}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b">
              <Th>Tipo</Th>
              <Th>Leads</Th>
              <Th right>Hablados</Th>
              <Th right>Ventas</Th>
              <Th right>Cierre</Th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.key} className="border-b last:border-0">
                <Td>{segmentLabel(dim, row.key)}</Td>
                <td className="w-1/3 px-2 py-1.5">
                  <div className="flex items-center gap-2">
                    <Bar
                      value={row.leads}
                      max={max}
                      title={`${row.leads} leads`}
                    />
                    <span className="w-8 text-right tabular-nums">
                      {row.leads}
                    </span>
                  </div>
                </td>
                <Td right>{percent(rate(row.talked, row.leads))}</Td>
                <Td right>{row.sold}</Td>
                <Td right>
                  <CloseCell sold={row.sold} leads={row.leads} />
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-[11px] text-muted-foreground">
        Con menos de {MIN_SAMPLE} leads no se pone nota: hay muy poca muestra.
      </p>
    </Card>
  );
};

/** Why they did not buy, and how the ones who bought paid. */
export const LostAndPaidCard = ({ perf }: { perf: Performance }) => {
  const lostTotal = perf.lost.reduce((sum, row) => sum + row.n, 0);
  const methods = Object.entries(perf.sales.by_method).sort(
    (a, b) => b[1] - a[1],
  );
  const sources = Object.entries(perf.sales.by_source).sort(
    (a, b) => b[1] - a[1],
  );
  return (
    <Card title="Por qué no compran y cómo pagan">
      <div className="grid gap-5 md:grid-cols-2">
        <div className="flex flex-col gap-2">
          <p className="text-xs font-medium text-muted-foreground">
            Motivos de «No compra» ({lostTotal})
          </p>
          {perf.lost.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Nadie en el periodo.
            </p>
          ) : (
            perf.lost.map((row) => (
              <div
                key={row.reason}
                className="grid grid-cols-[9.5rem_1fr_4.5rem] items-center gap-2"
              >
                <span className="text-sm">{lostLabel(row.reason)}</span>
                <Bar value={row.n} max={lostTotal} />
                <span className="text-right text-sm tabular-nums">
                  {row.n} · {percent(rate(row.n, lostTotal))}
                </span>
              </div>
            ))
          )}
          {perf.lost.some((row) => row.reason === "other") ? (
            <p className="text-[11px] text-muted-foreground">
              «Otro» no dice nada: pide a los setters que elijan el motivo real.
            </p>
          ) : null}
        </div>
        <div className="flex flex-col gap-2">
          <p className="text-xs font-medium text-muted-foreground">
            Ventas del periodo ({perf.sales.orders}) por forma de pago
          </p>
          {methods.map(([method, n]) => (
            <div
              key={method}
              className="grid grid-cols-[9.5rem_1fr_4.5rem] items-center gap-2"
            >
              <span className="text-sm">{methodLabel(method)}</span>
              <Bar value={n} max={perf.sales.orders} />
              <span className="text-right text-sm tabular-nums">{n}</span>
            </div>
          ))}
          {sources.length ? (
            <p className="text-xs text-muted-foreground">
              Por origen:{" "}
              {sources
                .map(([source, n]) => `${sourceLabel(source)} ${n}`)
                .join(" · ")}
            </p>
          ) : null}
          <p className="text-xs text-muted-foreground">
            Con mantenimiento:{" "}
            <b className="text-foreground">{perf.sales.with_maintenance}</b> de{" "}
            {perf.sales.orders}
          </p>
        </div>
      </div>
    </Card>
  );
};
